import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Extract a human-readable error message from an ApiService response.
 * ApiService functions never throw — they return the error object instead.
 */
function extractErrorMessage(res, fallback = 'Request failed') {
  if (axios.isAxiosError(res) && res.response) {
    const d = res.response.data;
    if (typeof d === 'string') return d;

    // Zod middleware shape: { error: "Validation failed", details: [{field, message}] }
    const detailMessages = Array.isArray(d?.details)
      ? d.details.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
      : [];
    if (detailMessages.length) return detailMessages.join('. ');

    // Standard error handler shape: { message: "...", errors: [{field, message}] }
    const errorMessages = Array.isArray(d?.errors)
      ? d.errors.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
      : [];
    if (errorMessages.length) return errorMessages.join('. ');

    return typeof d?.error === 'string' ? d.error
      : d?.error?.message || d?.msg || d?.message || fallback;
  }
  return res?.message || fallback;
}

/**
 * useApiQuery — TanStack Query wrapper for ApiService GET-like functions.
 *
 * USAGE
 * ─────
 *   const { data, isLoading, isError, refetch } = useApiQuery(
 *     ['employees', page, filter],   // cache key — changing any element triggers a refetch
 *     ApiService.getEmployees,       // ApiService function (payload, id?) => Promise
 *     { page, filter, userType },    // payload forwarded to the function as first argument
 *     { enabled: !!userId }          // optional TanStack Query options + { id }
 *   );
 *
 * MIGRATION from useApi + useEffect
 * ──────────────────────────────────
 *   BEFORE:
 *     const { action: load, data, loading } = useApi(ApiService.getEmployees);
 *     useEffect(() => { load({ page }); }, [page]);
 *
 *   AFTER:
 *     const { data, isLoading } = useApiQuery(
 *       ['employees', page],
 *       ApiService.getEmployees,
 *       { page }
 *     );
 *
 * @param {string | Array}  queryKey  TanStack cache key. Include every value the query depends on.
 * @param {Function}        apiFunc   An ApiService function: (payload, id?) => Promise
 * @param {Object}          [payload] Body/params forwarded to apiFunc. Default: {}.
 * @param {Object}          [options] Supports { id } plus any TanStack useQuery option.
 */
const useApiQuery = (queryKey, apiFunc, payload = {}, options = {}) => {
  const { id, ...queryOptions } = options;
  const key = Array.isArray(queryKey) ? queryKey : [queryKey];

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await apiFunc(payload, id);
      if (axios.isAxiosError(res) || res instanceof Error) {
        throw new Error(extractErrorMessage(res));
      }
      return res;
    },
    ...queryOptions,
  });
};

export default useApiQuery;
